import React from 'react';

import { DraggableProvided } from 'react-beautiful-dnd';

import { ROW_TYPE_KEYS } from 'api/queries/getRowTypeOptions.query';

import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';

import { CustomizedTooltip } from 'components/common';
import { ReadOnlyTooltipContent } from 'components/pages/ScheduleViewsPage/components/ReadOnlyTooltipContent/ReadOnlyTooltipContent';
import { FormValues } from 'components/pages/ScheduleViewsPage/components/ViewConfiguration/components/ViewConfigurationDrawerForm/components/RowConfig/RowConfig';
import classes from 'components/pages/ScheduleViewsPage/components/ViewConfiguration/components/ViewConfigurationDrawerForm/components/RowConfigView/RowConfigView.module.scss';
import { Pill } from 'components/pages/ScheduleViewsPage/components/ViewConfiguration/components/ViewConfigurationDrawerForm/components/RowConfigView/components/Pill/Pill';

const EDIT_DISABLED_TOOLTIP = "Save the row you're editing before editing or deleting another row.";

export interface RowConfigItem {
    data: FormValues;
}

interface Props {
    row: RowConfigItem;
    onEdit: () => void;
    onDelete: () => void;
    provided: DraggableProvided;
    isEditDisabled?: boolean;
    isViewOwner: boolean;
    isDragging?: boolean;
}

const formatBreadcrumb = (row: RowConfigItem) => {
    const parts = [row.data.project?.title, row.data.contract?.title].filter(Boolean);
    return parts.join(' > ') || '—';
};

export const RowConfigView = ({
    row,
    onEdit,
    onDelete,
    provided,
    isEditDisabled,
    isViewOwner,
    isDragging = false,
}: Props) => {
    const rowActionsDisabled = Boolean(isEditDisabled || !isViewOwner);
    const breadcrumb = formatBreadcrumb(row);
    const filterItemReferences = row.data.filterItemReferences;
    const selectActivitiesByValue = row.data.filterItem ?? undefined;
    const icon = row.data?.rowType === ROW_TYPE_KEYS.SUMMARY_BAR ? 'align_horizontal_left' : 'flag';
    const rowTitleText = row.data.rowName || 'Untitled row';

    return (
        <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            className={`${classes.viewCard}${isDragging ? ` ${classes.draggingListItem}` : ''}${
                !isViewOwner ? ` ${classes.viewCardNotOwner}` : ''
            }`}
        >
            <div className={classes.iconsColumn}>
                <CustomizedTooltip
                    tooltipContent={!isViewOwner ? <ReadOnlyTooltipContent /> : EDIT_DISABLED_TOOLTIP}
                    disabled={!rowActionsDisabled}
                    triggerElement={
                        <span
                            {...provided.dragHandleProps}
                            className={`${classes.dragHandle}${!isViewOwner ? ` ${classes.dragHandleDisabled}` : ''}`}
                            aria-hidden
                        >
                            <MuiIcon icon={'drag_indicator'} fontSize={'2rem'} type={'outlined'} />
                        </span>
                    }
                />

                <MuiIcon icon={icon} fontSize={'2rem'} color={'var(--text-quinary)'} type={'outlined'} />
            </div>
            <div className={classes.contentColumn}>
                <div className={classes.viewCardHeader}>
                    <span className={classes.rowTitle} title={rowTitleText}>
                        {rowTitleText}
                    </span>
                    <div className={classes.viewCardActions}>
                        <CustomizedTooltip
                            tooltipContent={!isViewOwner ? <ReadOnlyTooltipContent /> : EDIT_DISABLED_TOOLTIP}
                            disabled={!rowActionsDisabled}
                            triggerElement={
                                <span className={classes.iconButtonWrap}>
                                    <button
                                        type={'button'}
                                        className={classes.iconButton}
                                        data-testid={'row-config-view-edit-row'}
                                        disabled={rowActionsDisabled}
                                        aria-label={'Edit row'}
                                        onClick={onEdit}
                                    >
                                        <MuiIcon icon={'edit'} fontSize={'2rem'} type={'outlined'} />
                                    </button>
                                </span>
                            }
                        />
                        <CustomizedTooltip
                            tooltipContent={!isViewOwner ? <ReadOnlyTooltipContent /> : EDIT_DISABLED_TOOLTIP}
                            disabled={!rowActionsDisabled}
                            triggerElement={
                                <span className={classes.iconButtonWrap}>
                                    <button
                                        type={'button'}
                                        className={classes.iconButtonDanger}
                                        data-testid={'row-config-view-delete-row'}
                                        disabled={rowActionsDisabled}
                                        aria-label={'Delete row'}
                                        onClick={onDelete}
                                    >
                                        <MuiIcon
                                            icon={'delete'}
                                            fontSize={'2rem'}
                                            color={'var(--button-primary-bg_destructive)'}
                                            type={'outlined'}
                                        />
                                    </button>
                                </span>
                            }
                        />
                    </div>
                </div>
                <div className={classes.viewCardMeta}>
                    {breadcrumb && <span className={classes.breadcrumb}>{breadcrumb}</span>}
                </div>
                <div className={classes.viewCardTags}>
                    {selectActivitiesByValue && <Pill label={'Select activities by'} value={selectActivitiesByValue} />}
                    {filterItemReferences.length > 0 &&
                        filterItemReferences.map((ref) => (
                            <Pill key={ref.value} value={ref.label} brokenReferenceLink={ref.brokenReferenceLink} />
                        ))}
                </div>
            </div>
        </div>
    );
};
